import { addDays, parseISO } from 'date-fns';
import { formatLocalDate } from '@/lib/date-utils';

export type PeriodPreset = 'custom' | 'weekly' | 'biweekly' | 'semi-monthly' | 'monthly';

export interface PresetDates {
  start: string;
  end: string;
  payDate: string;
}

/** Days between the period end and the proposed pay date. */
export const PAY_DATE_OFFSET_DAYS = 5;

/** Backend `pay_week_start_day`: 0=Monday … 6=Sunday. */
export const PAY_WEEK_START_DAY_NAMES = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const;

export const DEFAULT_PAY_WEEK_START_DAY = 0;

export function normalizePayWeekStartDay(payWeekStartDay: number | null | undefined): number {
  if (
    payWeekStartDay === null ||
    payWeekStartDay === undefined ||
    !Number.isInteger(payWeekStartDay) ||
    payWeekStartDay < 0 ||
    payWeekStartDay > 6
  ) {
    return DEFAULT_PAY_WEEK_START_DAY;
  }
  return payWeekStartDay;
}

export function payWeekStartDayName(payWeekStartDay: number | null | undefined): string {
  return PAY_WEEK_START_DAY_NAMES[normalizePayWeekStartDay(payWeekStartDay)];
}

/** Backend 0=Monday … 6=Sunday → JS `Date#getDay()` Sunday=0 … Saturday=6. */
function toJsDayOfWeek(payWeekStartDay: number): number {
  return (payWeekStartDay + 1) % 7;
}

/**
 * End date of the most recent complete pay week on `today`: a pay week ends
 * the day before `pay_week_start_day`, and is complete once its end date is
 * on or before today. When today is the last day of a pay week, that week
 * counts as complete.
 */
function lastCompletePayWeekEnd(today: Date, payWeekStartDay: number): Date {
  const daysSinceWeekStart = (today.getDay() - toJsDayOfWeek(payWeekStartDay) + 7) % 7;
  const currentWeekStart = addDays(today, -daysSinceWeekStart);
  const currentWeekEnd = addDays(currentWeekStart, 6);
  return currentWeekEnd <= today ? currentWeekEnd : addDays(currentWeekStart, -1);
}

function clampPayDate(end: Date, today: Date): Date {
  const proposed = addDays(end, PAY_DATE_OFFSET_DAYS);
  return proposed < today ? today : proposed;
}

/**
 * Propose start / end / pay dates for a period preset.
 *
 * `orgToday` is today's date (yyyy-MM-dd) in the organization's time zone;
 * all arithmetic is on calendar dates, so no further time-zone handling is
 * needed here. Weekly and Biweekly align to the company's pay week; the pay
 * date is period end + PAY_DATE_OFFSET_DAYS but never before `orgToday`.
 */
export function getPresetDates(
  preset: PeriodPreset,
  orgToday: string,
  payWeekStartDay: number | null | undefined = DEFAULT_PAY_WEEK_START_DAY,
): PresetDates {
  if (preset === 'custom') {
    return { start: '', end: '', payDate: '' };
  }

  const today = parseISO(orgToday);
  const year = today.getFullYear();
  const month = today.getMonth();
  const weekStartDay = normalizePayWeekStartDay(payWeekStartDay);

  let start: Date;
  let end: Date;

  switch (preset) {
    case 'weekly': {
      end = lastCompletePayWeekEnd(today, weekStartDay);
      start = addDays(end, -6);
      break;
    }
    case 'biweekly': {
      end = lastCompletePayWeekEnd(today, weekStartDay);
      start = addDays(end, -13);
      break;
    }
    case 'semi-monthly': {
      if (today.getDate() <= 15) {
        start = new Date(year, month - 1, 16);
        end = new Date(year, month, 0);
      } else {
        start = new Date(year, month, 1);
        end = new Date(year, month, 15);
      }
      break;
    }
    case 'monthly': {
      start = new Date(year, month - 1, 1);
      end = new Date(year, month, 0);
      break;
    }
  }

  return {
    start: formatLocalDate(start),
    end: formatLocalDate(end),
    payDate: formatLocalDate(clampPayDate(end, today)),
  };
}
